import { common } from '@gosupersimple/types';

import { compact } from 'lodash';

import {
  Aggregation,
  CompositeFilterCondition,
  DereferencedPipelineOperation,
  ExpressionAggregation,
  FilterCondition,
  FilteringAggregation,
  GroupAggregateOperation,
  KeyedAggregation,
  MetricAggregation,
  PipelineOperation,
  RelationAggregateOperation,
  SortedAggregation,
  SwitchToRelationOperation,
} from '../types';

export const isKeyedAggregation = (aggregation: Aggregation): aggregation is KeyedAggregation =>
  'key' in aggregation && aggregation.key !== undefined;

export const isExprAggregation = (aggregation: Aggregation): aggregation is ExpressionAggregation =>
  aggregation.type === 'custom';

export const isMetricAggregation = (aggregation: Aggregation): aggregation is MetricAggregation =>
  aggregation.type === 'metric';

export const isSortedAggregation = (aggregation: Aggregation): aggregation is SortedAggregation =>
  'sort' in aggregation;

/**
 * Check if the aggregation supports filtering. Currently, for technical reasons, we do not support filter on first
 * and last aggregations even though they would make sense. This method checks if it is supported by the API.
 */
export const isFilteringAggregation = (
  aggregation: Aggregation,
): aggregation is FilteringAggregation =>
  ['count', 'count_distinct', 'sum', 'min', 'max', 'avg', 'median'].includes(aggregation.type);

/**
 * Return true if the aggregation has a filter
 */
export const isAggregationWithFilter = (
  aggregation: Aggregation,
): aggregation is FilteringAggregation & { filter: CompositeFilterCondition } =>
  isFilteringAggregation(aggregation) && aggregation.filter !== undefined;

export const isSingleFilterCondition = (
  condition: FilterCondition | CompositeFilterCondition,
): condition is FilterCondition => !('operands' in condition);

export const isValueExpression = (value: unknown): value is common.ExpressionValue => {
  return typeof value === 'object' && value !== null && 'expression' in value;
};

export const isValidOperation = (operation: PipelineOperation): boolean =>
  operation.operation !== 'invalid';

export const containsInvalidOperations = (operations: PipelineOperation[]): boolean =>
  !operations.every(isValidOperation);

export const isGroupAggregateOperation = (
  operation: PipelineOperation | DereferencedPipelineOperation,
): operation is GroupAggregateOperation => operation.operation === 'groupAggregate';

export const isRelationAggregateOperation = (
  operation: PipelineOperation | DereferencedPipelineOperation,
): operation is RelationAggregateOperation => operation.operation === 'relationAggregate';

export const isAggregatingOperation = (
  operation: PipelineOperation | DereferencedPipelineOperation,
) => isGroupAggregateOperation(operation) || isRelationAggregateOperation(operation);

export const isSwitchToRelationOperation = (
  operation: PipelineOperation | DereferencedPipelineOperation,
): operation is SwitchToRelationOperation => operation.operation === 'switchToRelation';

export const containsGroupAggregateOperation = (operations: DereferencedPipelineOperation[]) =>
  operations.some(isGroupAggregateOperation);

export const getLastGroupAggregate = (
  operations: PipelineOperation[] | DereferencedPipelineOperation[],
) => operations.findLast(isGroupAggregateOperation);

export const getLastGroupAggregateIndex = (
  operations: PipelineOperation[] | DereferencedPipelineOperation[],
) => operations.findLastIndex(isGroupAggregateOperation);

export const getLastSwitchToRelationIndex = (
  operations: PipelineOperation[] | DereferencedPipelineOperation[],
) => operations.findLastIndex(isSwitchToRelationOperation);

export const getCompositeConditionKeys = (condition: CompositeFilterCondition): string[] => {
  if ('operands' in condition) {
    return condition.operands.flatMap(getCompositeConditionKeys);
  }
  return [condition.key];
};

export const getAggregationKeys = (aggregations: Aggregation[]): string[] => {
  return compact([
    ...aggregations.filter(isKeyedAggregation).map((aggregation) => aggregation.key),
    ...aggregations
      .filter(isSortedAggregation)
      .flatMap((aggregation) =>
        aggregation.sort !== undefined ? getSortingKeys(aggregation.sort) : null,
      ),
  ]);
};

export const getSortingKeys = (sorting: common.Sorting[]) => {
  return sorting.map((sort) => sort.key);
};

export const getGroupingKeys = (grouping: common.Grouping[]) => {
  return grouping.map((group) => group.key);
};

export const getAggregationPropertyKeys = (aggregations: Aggregation[]) => {
  return aggregations.map((aggregation) => aggregation.property.key);
};

export const getOperationPropertyKeys = (
  operation: PipelineOperation | DereferencedPipelineOperation,
) => {
  switch (operation.operation) {
    case 'deriveField':
      return [operation.parameters.key];
    case 'addRelatedColumn':
      return operation.parameters.columns.map((column) => column.property.key);
    case 'groupAggregate':
      return getAggregationPropertyKeys(operation.parameters.aggregations);
    case 'relationAggregate':
      return getAggregationPropertyKeys(operation.parameters.aggregations);
    default:
      return [];
  }
};
